'use client';

import React, { useState } from 'react';
import {
  User,
  Lock,
  Bell,
  Shield,
  Camera,
  Save,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Building2,
  ChevronRight,
} from 'lucide-react';
import { useRole } from '@/lib/useRole';
import { ROLE_PERMISSIONS, Permission } from '@/lib/rbac';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
type Tab = 'account' | 'password' | 'notifications' | 'role';

interface NotificationPref {
  id: string;
  label: string;
  description: string;
  email: boolean;
  push: boolean;
  sms: boolean;
}

// ─── Permission display labels ────────────────────────────────────────────────
const PERMISSION_LABELS: Record<Permission, string> = {
  view_dashboard: 'View Dashboard',
  view_farms: 'View Farms',
  manage_farms: 'Manage Farms',
  view_flocks: 'View Flocks',
  manage_flocks: 'Manage Flocks',
  view_daily_records: 'View Daily Records',
  create_daily_records: 'Create Daily Records',
  view_health: 'View Health Records',
  manage_health: 'Manage Health Records',
  view_mortality: 'View Mortality Records',
  create_mortality: 'Create Mortality Records',
  view_inventory: 'View Inventory',
  manage_inventory: 'Manage Inventory',
  view_sales: 'View Sales',
  manage_sales: 'Manage Sales',
  create_invoices: 'Create Invoices',
  view_expenses: 'View Expenses',
  create_expenses: 'Create Expenses',
  approve_expenses: 'Approve Expenses',
  view_finance: 'View Finance',
  manage_finance: 'Manage Finance',
  view_payroll: 'View Payroll',
  manage_payroll: 'Manage Payroll',
  view_employees: 'View Employees',
  manage_employees: 'Manage Employees',
  view_reports: 'View Reports',
  view_analytics: 'View Analytics',
  manage_settings: 'Manage Settings',
  view_audit_logs: 'View Audit Logs',
  manage_procurement: 'Manage Procurement',
  view_procurement: 'View Procurement',
};

const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  { label: 'Farm & Operations', permissions: ['view_farms', 'manage_farms', 'view_flocks', 'manage_flocks', 'view_daily_records', 'create_daily_records'] },
  { label: 'Health & Mortality', permissions: ['view_health', 'manage_health', 'view_mortality', 'create_mortality'] },
  { label: 'Inventory & Supply', permissions: ['view_inventory', 'manage_inventory', 'view_procurement', 'manage_procurement'] },
  { label: 'Commercial', permissions: ['view_sales', 'manage_sales', 'create_invoices', 'view_expenses', 'create_expenses', 'approve_expenses', 'view_finance', 'manage_finance'] },
  { label: 'People & System', permissions: ['view_employees', 'manage_employees', 'view_payroll', 'manage_payroll', 'view_reports', 'view_analytics', 'manage_settings', 'view_audit_logs'] },
];

const DEFAULT_NOTIFICATIONS: NotificationPref[] = [
  { id: 'notif-mortality', label: 'Mortality Alerts', description: 'High mortality rate warnings for flocks', email: true, push: true, sms: true },
  { id: 'notif-vaccination', label: 'Vaccination Reminders', description: 'Upcoming and overdue vaccination schedules', email: true, push: true, sms: false },
  { id: 'notif-inventory', label: 'Low Stock Alerts', description: 'Inventory items falling below reorder level', email: true, push: false, sms: false },
  { id: 'notif-expenses', label: 'Expense Approvals', description: 'Pending expense approval requests', email: true, push: true, sms: false },
  { id: 'notif-sales', label: 'Sales & Invoices', description: 'New invoices and overdue payment reminders', email: true, push: false, sms: false },
  { id: 'notif-reports', label: 'Weekly Reports', description: 'Automated weekly farm performance summaries', email: true, push: false, sms: false },
  { id: 'notif-tasks', label: 'Task Assignments', description: 'When tasks are assigned or updated', email: false, push: true, sms: false },
  { id: 'notif-system', label: 'System Updates', description: 'Platform maintenance and feature announcements', email: true, push: false, sms: false },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function TabButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: React.ElementType; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
        active ? 'text-white' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
      style={active ? { backgroundColor: 'var(--primary)' } : {}}
    >
      <Icon size={16} className="flex-shrink-0" />
      <span>{label}</span>
      {active && <ChevronRight size={14} className="ml-auto opacity-60" />}
    </button>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none"
      style={{ backgroundColor: checked ? 'var(--primary)' : 'var(--border)' }}
      role="switch"
      aria-checked={checked}
    >
      <span
        className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
        style={{ transform: checked ? 'translateX(16px)' : 'translateX(0)' }}
      />
    </button>
  );
}

// ─── Account Tab ─────────────────────────────────────────────────────────────
function AccountTab() {
  const { user } = useRole();
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    phone: '+256 700 123 456',
    location: 'Kampala, Uganda',
    farmAssignment: 'Idudi Poultry Farm',
    bio: 'Experienced farm manager overseeing daily operations, flock health, and production targets across multiple branches.',
  });

  function handleChange(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  }

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex items-center gap-5 p-5 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <div className="relative">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            {user.initials}
          </div>
          <button
            className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center text-white shadow-md transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--accent)' }}
            title="Change photo"
          >
            <Camera size={13} />
          </button>
        </div>
        <div>
          <div className="font-semibold text-foreground text-lg">{form.name}</div>
          <div className="text-sm text-muted-foreground">{form.email}</div>
          <div className="text-xs mt-1 text-muted-foreground">Profile photo · JPG, PNG up to 2 MB</div>
        </div>
      </div>

      {/* Form */}
      <div className="p-5 rounded-xl border space-y-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <h3 className="text-sm font-semibold text-foreground">Personal Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name</label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                className="input-field pl-9 w-full text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email Address</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                className="input-field pl-9 w-full text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Phone Number</label>
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="tel"
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                className="input-field pl-9 w-full text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Location</label>
            <div className="relative">
              <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={form.location}
                onChange={e => handleChange('location', e.target.value)}
                className="input-field pl-9 w-full text-sm"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Farm Assignment</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={form.farmAssignment}
                readOnly
                className="input-field pl-9 w-full text-sm bg-muted cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Farm assignment is managed by your administrator.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Bio</label>
            <textarea
              value={form.bio}
              onChange={e => handleChange('bio', e.target.value)}
              rows={3}
              className="input-field w-full text-sm resize-none"
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
          {saved ? (
            <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--success)' }}>
              <Check size={14} /> Changes saved
            </span>
          ) : (
            <span />
          )}
          <button onClick={handleSave} className="btn-primary text-sm flex items-center gap-2">
            <Save size={14} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Password Tab ─────────────────────────────────────────────────────────────
function PasswordTab() {
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [show, setShow] = useState({ current: false, newPass: false, confirm: false });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  function handleChange(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    setStatus('idle');
  }

  function getStrength(pass: string): { label: string; color: string; width: string } {
    if (!pass) return { label: '', color: 'var(--border)', width: '0%' };
    if (pass.length < 6) return { label: 'Too short', color: 'var(--danger)', width: '20%' };
    if (pass.length < 8) return { label: 'Weak', color: 'var(--warning)', width: '40%' };
    const hasUpper = /[A-Z]/.test(pass);
    const hasNum = /[0-9]/.test(pass);
    const hasSpecial = /[^A-Za-z0-9]/.test(pass);
    const score = [hasUpper, hasNum, hasSpecial].filter(Boolean).length;
    if (score === 3 && pass.length >= 12) return { label: 'Strong', color: 'var(--success)', width: '100%' };
    if (score >= 2) return { label: 'Good', color: 'var(--accent)', width: '75%' };
    return { label: 'Fair', color: 'var(--warning)', width: '55%' };
  }

  const strength = getStrength(form.newPass);

  function handleSubmit() {
    if (!form.current) { setStatus('error'); setErrorMsg('Please enter your current password.'); return; }
    if (form.newPass.length < 8) { setStatus('error'); setErrorMsg('New password must be at least 8 characters.'); return; }
    if (form.newPass !== form.confirm) { setStatus('error'); setErrorMsg('New passwords do not match.'); return; }
    setStatus('success');
    setForm({ current: '', newPass: '', confirm: '' });
  }

  return (
    <div className="space-y-5">
      <div className="p-5 rounded-xl border space-y-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <h3 className="text-sm font-semibold text-foreground">Change Password</h3>
        <p className="text-xs text-muted-foreground">Use a strong password with at least 8 characters, including uppercase letters, numbers, and symbols.</p>

        {status === 'success' && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--success-bg)', color: 'var(--success)' }}>
            <Check size={15} /> Password updated successfully.
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--danger-bg)', color: 'var(--danger)' }}>
            <AlertCircle size={15} /> {errorMsg}
          </div>
        )}

        <div className="space-y-3">
          {/* Current password */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={show.current ? 'text' : 'password'}
                value={form.current}
                onChange={e => handleChange('current', e.target.value)}
                placeholder="Enter current password"
                className="input-field w-full text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShow(s => ({ ...s, current: !s.current }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {show.current ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={show.newPass ? 'text' : 'password'}
                value={form.newPass}
                onChange={e => handleChange('newPass', e.target.value)}
                placeholder="Enter new password"
                className="input-field w-full text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShow(s => ({ ...s, newPass: !s.newPass }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {show.newPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {form.newPass && (
              <div className="mt-2 space-y-1">
                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: strength.width, backgroundColor: strength.color }}
                  />
                </div>
                <span className="text-xs font-medium" style={{ color: strength.color }}>{strength.label}</span>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm New Password</label>
            <div className="relative">
              <input
                type={show.confirm ? 'text' : 'password'}
                value={form.confirm}
                onChange={e => handleChange('confirm', e.target.value)}
                placeholder="Re-enter new password"
                className="input-field w-full text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShow(s => ({ ...s, confirm: !s.confirm }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {show.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {form.confirm && form.newPass && form.confirm !== form.newPass && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>Passwords do not match</p>
            )}
          </div>
        </div>

        <div className="pt-2 border-t flex justify-end" style={{ borderColor: 'var(--border)' }}>
          <button onClick={handleSubmit} className="btn-primary text-sm flex items-center gap-2">
            <Lock size={14} />
            Update Password
          </button>
        </div>
      </div>

      {/* Security tips */}
      <div className="p-4 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <h4 className="text-xs font-semibold text-foreground mb-3">Password Requirements</h4>
        <ul className="space-y-1.5">
          {[
            'At least 8 characters long',
            'At least one uppercase letter (A–Z)',
            'At least one number (0–9)',
            'At least one special character (!@#$...)',
          ].map((req, i) => (
            <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--muted-foreground)' }} />
              {req}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Notifications Tab ────────────────────────────────────────────────────────
function NotificationsTab() {
  const [prefs, setPrefs] = useState<NotificationPref[]>(DEFAULT_NOTIFICATIONS);
  const [saved, setSaved] = useState(false);

  function toggle(id: string, channel: 'email' | 'push' | 'sms') {
    setPrefs(prev => prev.map(p => p.id === id ? { ...p, [channel]: !p[channel] } : p));
    setSaved(false);
  }

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <div className="space-y-5">
      <div className="p-5 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Notification Preferences</h3>
          <div className="hidden sm:grid grid-cols-3 gap-6 text-xs font-semibold text-muted-foreground uppercase tracking-wide" style={{ minWidth: '180px', fontSize: '0.65rem' }}>
            <span className="text-center">Email</span>
            <span className="text-center">Push</span>
            <span className="text-center">SMS</span>
          </div>
        </div>

        <div className="space-y-1">
          {prefs.map((pref, idx) => (
            <div
              key={pref.id}
              className={`flex items-center justify-between py-3 ${idx < prefs.length - 1 ? 'border-b' : ''}`}
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex-1 min-w-0 pr-4">
                <div className="text-sm font-medium text-foreground">{pref.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{pref.description}</div>
              </div>
              <div className="grid grid-cols-3 gap-6" style={{ minWidth: '180px' }}>
                <div className="flex justify-center">
                  <ToggleSwitch checked={pref.email} onChange={() => toggle(pref.id, 'email')} />
                </div>
                <div className="flex justify-center">
                  <ToggleSwitch checked={pref.push} onChange={() => toggle(pref.id, 'push')} />
                </div>
                <div className="flex justify-center">
                  <ToggleSwitch checked={pref.sms} onChange={() => toggle(pref.id, 'sms')} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
          {saved ? (
            <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--success)' }}>
              <Check size={14} /> Preferences saved
            </span>
          ) : (
            <span />
          )}
          <button onClick={handleSave} className="btn-primary text-sm flex items-center gap-2">
            <Save size={14} />
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Role & Permissions Tab ───────────────────────────────────────────────────
function RoleTab() {
  const { user, role, roleLabel, roleColors } = useRole();

  // Get permissions for current role from ROLE_PERMISSIONS (via rbac)
  // We reconstruct by checking hasPermission for each known permission
  const allPermissions = Object.keys(PERMISSION_LABELS) as Permission[];
  const userPermissions = new Set(
    allPermissions.filter(p => {
      // Use the ROLE_PERMISSIONS map indirectly via the exported constant
      const rolePerms: Record<string, Permission[]> = {
        farm_owner: ['view_dashboard','view_farms','manage_farms','view_flocks','manage_flocks','view_daily_records','create_daily_records','view_health','manage_health','view_mortality','create_mortality','view_inventory','manage_inventory','view_sales','manage_sales','create_invoices','view_expenses','create_expenses','approve_expenses','view_finance','manage_finance','view_payroll','manage_payroll','view_employees','manage_employees','view_reports','view_analytics','manage_settings','view_audit_logs','view_procurement','manage_procurement'],
        manager: ['view_dashboard','view_farms','manage_farms','view_flocks','manage_flocks','view_daily_records','create_daily_records','view_health','manage_health','view_mortality','create_mortality','view_inventory','manage_inventory','view_sales','manage_sales','create_invoices','view_expenses','create_expenses','approve_expenses','view_finance','view_employees','manage_employees','view_reports','view_analytics','view_audit_logs','view_procurement','manage_procurement'],
        technician: ['view_dashboard','view_farms','view_flocks','manage_flocks','view_daily_records','create_daily_records','view_health','manage_health','view_mortality','create_mortality','view_inventory','view_sales','view_expenses','create_expenses','view_reports','view_procurement'],
        field_staff: ['view_dashboard','view_farms','view_flocks','view_daily_records','create_daily_records','view_health','view_mortality','create_mortality','view_inventory'],
      };
      return (rolePerms[role] as Permission[])?.includes(p) ?? false;
    })
  );

  const ROLE_DESCRIPTIONS: Record<string, string> = {
    farm_owner: 'Full access to all farm operations, finance, payroll, settings, and audit logs. Can manage all users and approve any action.',
    manager: 'Broad operational access including finance, procurement, and employee management. Can approve expenses and create invoices.',
    technician: 'Access to farm operations, health records, and inventory viewing. Can create expenses but cannot approve them.',
    field_staff: 'Limited to daily record entry, viewing flocks, health records, and mortality logging. No financial access.',
  };

  return (
    <div className="space-y-5">
      {/* Role card */}
      <div className="p-5 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <h3 className="text-sm font-semibold text-foreground mb-4">Assigned Role</h3>
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--success-bg)' }}
          >
            <Shield size={22} style={{ color: 'var(--primary)' }} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-foreground">{roleLabel}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${roleColors.bg} ${roleColors.text}`}>
                {roleLabel}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              {ROLE_DESCRIPTIONS[role]}
            </p>
            <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
              <span>Assigned to: <span className="font-medium text-foreground">{user.name}</span></span>
              <span>Farm: <span className="font-medium text-foreground">Idudi Poultry Farm</span></span>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <AlertCircle size={12} />
            Role assignments are managed by your Farm Owner or system administrator.
          </p>
        </div>
      </div>

      {/* Permissions matrix */}
      <div className="p-5 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Permissions</h3>
          <span className="text-xs text-muted-foreground">{userPermissions.size} of {allPermissions.length} permissions granted</span>
        </div>
        <div className="space-y-5">
          {PERMISSION_GROUPS.map(group => (
            <div key={group.label}>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2" style={{ fontSize: '0.65rem' }}>
                {group.label}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {group.permissions.map(perm => {
                  const granted = userPermissions.has(perm);
                  return (
                    <div
                      key={perm}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs"
                      style={{
                        backgroundColor: granted ? 'var(--success-bg)' : 'var(--muted)',
                        color: granted ? 'var(--success)' : 'var(--muted-foreground)',
                      }}
                    >
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: granted ? 'var(--success)' : 'var(--border)' }}
                      >
                        {granted && <Check size={9} color="white" strokeWidth={3} />}
                      </div>
                      <span className="font-medium">{PERMISSION_LABELS[perm]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProfileContent() {
  const [activeTab, setActiveTab] = useState<Tab>('account');
  const { user, roleLabel, roleColors } = useRole();

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'account', label: 'Account Details', icon: User },
    { id: 'password', label: 'Change Password', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'role', label: 'Role & Permissions', icon: Shield },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">My Profile</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Manage your account details, security, and notification preferences.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Sidebar nav */}
        <div className="lg:w-52 flex-shrink-0">
          <div className="p-4 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
            {/* User summary */}
            <div className="flex items-center gap-3 pb-4 mb-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                {user.initials}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{user.name}</div>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${roleColors.bg} ${roleColors.text}`}>
                  {roleLabel}
                </span>
              </div>
            </div>
            {/* Tab buttons */}
            <div className="space-y-0.5">
              {tabs.map(tab => (
                <TabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  icon={tab.icon}
                  label={tab.label}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'account' && <AccountTab />}
          {activeTab === 'password' && <PasswordTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'role' && <RoleTab />}
        </div>
      </div>
    </div>
  );
}
